import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, tap, mergeMap } from 'rxjs/operators';
import { AuthenticationService } from '@app/core/services/authentication/authentication.service';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { Credentials, User } from '@app/core/models/user/user.model';
import { SnackbarOpen } from '../snackbar/snackbar.actions';
import { PORTAL_ROUTE_NAMES } from '@app/modules/portal/portal.routes.names';
import { Action } from '@ngrx/store';
import { HideSpinner, ShowSpinner } from '../loading-spinner/loading-spinner.actions';
import { AUTH_ROUTE_NAMES } from '@app/modules/auth/auth.routes.names';
import { AuthTokenService } from '@app/core/services/auth-token/auth-token.service';
import {
  LogIn,
  AssistedModeLogin,
  LogInSuccess,
  LogInFailure,
  LOGIN,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_REDIRECT,
  FIRST_ACCESS_LOGIN,
  LOGOUT,
  ASSISTED_MODE_LOGIN,
  AssistedModeCpfClientLogIn,
  ASSISTED_MODE_CLIENT_LOGIN,
} from '@app/store/auth/auth.actions';
import { parseUserPayload } from './auth.reducer';
// import { FeedbackDialogComponent } from '@app/theme/feedback-dialog/feedback-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackDialogComponent } from '@app/theme/feedback-dialog/feedback-dialog.component';
import { TermsService } from '@app/core/services/terms/terms.service';
import { SignInWarningComponent } from '@app/modules/auth/pages/sign-in/sign-in-warning/sign-in-warning.component';

const showSpinnerActions = [LOGIN, FIRST_ACCESS_LOGIN];

const hideSpinnerActions = [LOGIN_SUCCESS, LOGIN_FAILURE];

@Injectable()
export class AuthEffects {
  public LOCAL_STORAGE_BALANCE = 'SABEMI_SHOW_BALANCE';
  constructor(
    private readonly actions: Actions,
    private readonly authenticationService: AuthenticationService,
    private readonly authTokenService: AuthTokenService,
    private readonly router: Router,
    public dialog: MatDialog,
    private termsService: TermsService
  ) {}

  @Effect()
  showSpinner: Observable<Action> = this.actions.pipe(
    ofType(...showSpinnerActions),
    map(() => new ShowSpinner())
  );

  @Effect()
  hideSpinner: Observable<Action> = this.actions.pipe(
    ofType(...hideSpinnerActions),
    map(() => new HideSpinner())
  );

  @Effect()
  logIn: Observable<any> = this.actions.pipe(
    ofType(LOGIN),
    map((action: LogIn) => action.payload),
    exhaustMap(({ cpf, password, beTips, beTerms, beOffer }: Credentials) => {
      return this.authenticationService
        .signIn({ cpf, password, beTips, beTerms, beOffer })
        .pipe(
          map(({ data: { accessToken, refreshToken } }) =>
            this.handleUserLogInSuccess(accessToken, refreshToken)
          ),
          catchError((error) => of(new LogInFailure(error)))
        );
    })
  );

  @Effect()
  AssistedModeLogin: Observable<any> = this.actions.pipe(
    ofType(ASSISTED_MODE_LOGIN),
    map((action: AssistedModeLogin) => action.payload),
    exhaustMap(({ cpf, password }: Credentials) => {
      return this.authenticationService.assistedModeSignIn({ cpf, password }).pipe(
        map(({ data: { accessToken, refreshToken } }) =>
          this.handleUserLogInSuccess(accessToken, refreshToken)
        ),
        catchError((error) => of(new LogInFailure(error)))
      );
    })
  );

  @Effect()
  AssistedModeCpfClientLogIn: Observable<any> = this.actions.pipe(
    ofType(ASSISTED_MODE_CLIENT_LOGIN),
    map((action: AssistedModeCpfClientLogIn) => action.payload),
    exhaustMap(({ cpf }: Credentials) => {
      return this.authenticationService.assistedModeCpfClientSignIn({ cpf }).pipe(
        map(({ data: { accessToken, refreshToken } }) => {
          this.authenticationService.setCpfUserFirstAccess(cpf);
          localStorage.setItem('isAssistedMode', 'true');
          return this.handleUserLogInSuccess(accessToken, refreshToken);
        }),
        catchError((error) => of(new LogInFailure(error)))
      );
    })
  );

  @Effect()
  firstAccessLogIn: Observable<any> = this.actions.pipe(
    ofType(FIRST_ACCESS_LOGIN),
    map((action: LogIn) => action.payload),
    exhaustMap(({ cpf }: Credentials) => {
      return this.authenticationService.firstAccessSignIn({ cpf }).pipe(
        map(({ data: { accessToken, refreshToken } }) => {
          this.authenticationService.setCpfUserFirstAccess(cpf);
          return this.handleUserLogInSuccess(accessToken, refreshToken);
        }),
        catchError((error) => of(new LogInFailure(error)))
      );
    })
  );

  @Effect()
  logInSuccess: Observable<any> = this.actions.pipe(
    ofType(LOGIN_SUCCESS),
    map((action: LogInSuccess) => action.payload),
    tap((loggedUser: User) => {
      if (!loggedUser.firstAccess) {
        return this.router.navigateByUrl(PORTAL_ROUTE_NAMES.DASHBOARD);
      }

      return this.router.navigateByUrl(AUTH_ROUTE_NAMES.VERIFY_ACCOUNT);
    }),
    map(
      () =>
        new SnackbarOpen({
          message: 'Bem Vindo!',
        })
      // console.log('bem vindo')
      // this.openFeedbackDialog('success', 'Bem Vindo!')
    )
  );

  @Effect()
  logInFailure: Observable<any> = this.actions.pipe(
    ofType(LOGIN_FAILURE),
    map((action: LogInFailure) => action.payload),
    mergeMap(({ error: { errors = [] } }) => {
      let errorActions: Action[] = [];

      errors.map((error) => {
        errorActions = [
          ...errorActions,
          new SnackbarOpen({
            message: error,
          }),
        ];
        // MODAL OK MODAL, API?
        // this.openFeedbackDialog('error', error);
        this.openFeedbackDialog('warning', error);
      });

      return errorActions;
    })
  );

  @Effect()
  logInRedirect: Observable<any> = this.actions.pipe(
    ofType(LOGIN_REDIRECT),
    tap(() => this.router.navigateByUrl(AUTH_ROUTE_NAMES.LOGIN)),
    map(
      () =>
        new SnackbarOpen({
          message: 'Realize o login para entrar no portal',
        })
      // this.openFeedbackDialog('info', 'Realize o login para entrar no portal')
    )
  );

  @Effect({
    dispatch: false,
  })
  logOut: Observable<any> = this.actions.pipe(
    ofType(LOGOUT),
    tap(() => {
      this.authenticationService.logOut();

      this.router.navigateByUrl(AUTH_ROUTE_NAMES.LOGIN);
    })
  );

  handleUserLogInSuccess = (
    accessToken: string,
    userRefreshToken: string
  ): LogInSuccess => {
    const objTermLogin = JSON.parse(localStorage.getItem('objTermLogin') || 'null');
    if (objTermLogin) {
      const newCodTerm = Number(objTermLogin.termCode);
      const payload = {
        codFormulario: objTermLogin.formId,
        codTermo: newCodTerm,
        cpf: objTermLogin.cpf,
        ip: objTermLogin.ipAddress,
      };
      this.termsService.sendTerms(payload).subscribe(() => {
        localStorage.removeItem('objTermLogin');
      });
    }

    this.router.navigateByUrl(AUTH_ROUTE_NAMES.ASSISTED_MODE_CLIENT_LOGIN);
    this.openWarningDialog(SignInWarningComponent);

    this.authTokenService.setAccessTokens(accessToken, userRefreshToken);

    const authenticatedUser: User = this.authTokenService.decodeAccessToken();

    return new LogInSuccess(parseUserPayload({ ...authenticatedUser, accessToken }));
  };

  openFeedbackDialog(color, message) {
    this.openFeedbackDialogConfig(FeedbackDialogComponent, {
      data: {
        color: color,
        message: message,
      },
    });
  }

  openFeedbackDialogConfig(dialogComponent, data = {}) {
    return this.dialog.open(dialogComponent, {
      data,
      panelClass: 'feedback-modalbox',
      disableClose: true,
    });
  }

  openWarningDialog(dialogComopnent) {
    return this.dialog.open(dialogComopnent, 
      {
        maxWidth: 700,
        height: '800'
      });
  }
}
